// ** Redux Imports
import { createAsyncThunk, createSlice } from '@reduxjs/toolkit'

// ** Axios Imports
import * as rolesApi from '../api'

/**
 * Llave con la que este reducer está montado en el rootReducer.
 *
 * No coincide con el `name` del slice ('appRoles'): ese sólo prefija los action
 * types. Todo acceso al store (condition y selectores) debe pasar por aquí.
 */
export const ROLES_STATE_KEY = 'roles'

const selectRolesSlice = (state) => state[ROLES_STATE_KEY]

//Tiempo de vida del catálogo de roles en el store (100 minutos)
export const ROLES_CACHE_TTL = 100 * 60 * 1000

/**
 * Lee el catálogo de roles.
 *
 * Para forzar una lectura saltándose el caché: `dispatch(getRoles({ force: true }))`
 * —o `dispatch(forceFetchRoles())` cuando quien invalida no es quien pide—.
 */
export const getRoles = createAsyncThunk(
    'appRoles/getRoles',
    async (_, { rejectWithValue }) => {
        try {
            return {
                data: await rolesApi.getRoles()
            }
        } catch (error) {
            return rejectWithValue(error.response?.data)
        }
    },
    {
        condition: (arg, { getState }) => {
            const roles = selectRolesSlice(getState() ?? {})

            if (!roles) return true

            // Ya hay una lectura en vuelo (las escrituras usan `addStatus`)
            if (roles.status === 'pending') return false

            // Invalidación explícita (`{ force: true }` o forceFetchRoles) o store vacío
            if (arg?.force || roles.forceFetch || !roles.data?.length) return true

            // Verificar si el cache aún es válido
            return Date.now() - (roles.lastFetch ?? 0) > ROLES_CACHE_TTL
        }
    }
)

export const addRole = createAsyncThunk(
    'appRoles/addRole',
    async (role, { dispatch, rejectWithValue }) => {
        try {
            const response = await rolesApi.addRole(role)
            //La lista se recarga del backend en vez de insertar el rol a mano:
            //el POST no garantiza devolver la misma forma que un elemento del GET.
            //No se hace `unwrap`: si la recarga falla, la creación sigue siendo un éxito
            //y getRoles.rejected ya deja el store listo para reintentar
            await dispatch(getRoles({ force: true }))

            return response
        } catch (error) {
            return rejectWithValue(error.response?.data)
        }
    }
)

export const appRolesSlice = createSlice({
    name: 'appRoles',
    initialState: {
        data: [],
        // Estado de la lectura del catálogo
        status: 'idle', // idle | pending | fulfilled | rejected
        error: null,
        // Estado de la escritura, separado para que crear un rol no aborte ni
        // ponga en "cargando" a la lista
        addStatus: 'idle', // idle | pending | fulfilled | rejected
        addError: null,
        lastFetch: null,
        forceFetch: false
    },
    reducers: {
        resetError: (state) => {
            state.error = null
            state.addError = null
        },
        forceFetchRoles: (state) => {
            state.forceFetch = true
        },
        resetRoles: (state) => {
            state.data = []
            state.status = 'idle'
            state.error = null
            state.addStatus = 'idle'
            state.addError = null
            state.lastFetch = null
            state.forceFetch = false
        }
    },
    extraReducers: builder => {
        builder
            // GET ROLES
            .addCase(getRoles.pending, (state) => {
                state.status = 'pending'
                state.error = null
                //Se consume aquí para que una invalidación durante la petición
                //siga vigente: la respuesta en vuelo no la describe
                state.forceFetch = false
            })
            .addCase(getRoles.fulfilled, (state, action) => {
                state.status = 'fulfilled'
                //El endpoint debe devolver una lista: cualquier otra forma dejaría
                //la pantalla con un `data.map` roto
                state.data = Array.isArray(action.payload.data) ? action.payload.data : []
                state.lastFetch = Date.now()
                state.error = null
            })
            .addCase(getRoles.rejected, (state, action) => {
                state.status = 'rejected'
                //Sin respuesta HTTP (timeout, CORS, backend caído) el payload viene
                //vacío: se cae al mensaje del propio error para no guardar `undefined`
                state.error = action.payload ?? action.error?.message ?? null
                // Permite reintentar de inmediato: si el fallo ocurrió en un
                // refetch con datos previos, el TTL bloquearía la siguiente
                // petición durante todo el periodo de caché
                state.forceFetch = true
            })

            // ADD ROLE
            .addCase(addRole.pending, (state) => {
                state.addStatus = 'pending'
                state.addError = null
            })
            //`data` y `lastFetch` los actualiza la recarga que dispara el thunk,
            //así el caché nunca se renueva con un dato que el backend no confirmó
            .addCase(addRole.fulfilled, (state) => {
                state.addStatus = 'fulfilled'
                state.addError = null
            })
            .addCase(addRole.rejected, (state, action) => {
                state.addStatus = 'rejected'
                state.addError = action.payload ?? action.error?.message ?? null
            })
    }
})

// Selectores — lectura
export const selectRoles = (state) => selectRolesSlice(state).data
export const selectRolesStatus = (state) => selectRolesSlice(state).status
export const selectRolesError = (state) => selectRolesSlice(state).error
export const selectRolesLoading = (state) => selectRolesSlice(state).status === 'pending'

// Selectores — escritura
export const selectAddRoleStatus = (state) => selectRolesSlice(state).addStatus
export const selectAddRoleLoading = (state) => selectRolesSlice(state).addStatus === 'pending'
export const selectAddRoleError = (state) => selectRolesSlice(state).addError

export const { resetError, forceFetchRoles, resetRoles } = appRolesSlice.actions

export default appRolesSlice.reducer
